import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { completeTask, updateTask, type Task, type TaskStatus } from '../api';
import { STATUS_LOOK, STATUS_VERB, displayStatus } from '../taskVocab';
import { StatusBadge } from './StatusBadge';
import { Icon } from './Icon';

/**
 * Which readback a press put up, so its refusal can take down that one and no other.
 *
 * Whatever the listener chose to hand out from `onAct` — a counter on both screens today — and
 * `undefined` when it put nothing up. A listener compares it for identity and reads nothing else.
 */
export type ReadbackClaim = number | undefined;

/**
 * The row's box is a checkbox, and a checkbox means done.
 *
 * It used to be one box carrying the whole progression — **not started → started → done**, one
 * press each, wrapping round, with a press-and-hold reset to `inbox`. The argument for that was
 * that two controls for one linear progression was one too many, and the box was the thing
 * everybody reaches for. Three rounds of persona evidence said the same argument the other way:
 * finishing cost two clicks, the first of which filed the task into a status nobody asked for; the
 * play triangle was drawn on the control that *completes*; `next` and `in_progress` rows offered
 * two identical squares labelled "Start …" and "Complete …", told apart by a border colour; and a
 * tap in Select mode started a task instead of selecting it.
 *
 * The decisive one is not a preference. `keys.ts` binds `x` to **complete** on every row whatever
 * its state, so the mouse and the keyboard disagreed about what the row's primary act is — a rule
 * stated on one surface and contradicted on another. This makes the box the thing its own ARIA
 * already claimed it was.
 *
 * So: **one press completes**, and a second on a finished row reopens it, which is what a checkbox
 * does and is also the board's "Done today" strip's undo. Starting moved to `TaskProgressMark`
 * below. The long press is gone with the cycle it reset — the list's readback toast is the undo
 * now, and it is visible, keyboard-reachable and says which task it is about, none of which a
 * press-and-hold was.
 *
 * `role="checkbox"` on a `<button>` rather than an `<input type="checkbox">` is inherited from the
 * three-state version and stays: the row is a flex line whose other controls are buttons, and a
 * native checkbox brings a UA-drawn box that has to be unstyled before it can be drawn again.
 * `aria-checked` is now a plain boolean, which is what it always should have said.
 *
 * In Select mode the same box is the **selection** checkbox. The mode was built for the phone,
 * where `Space`, shift-click and ctrl-click are not available, but its toggle is on the toolbar at
 * every width (see `selecting` in `TaskList`) — so at a desk this circle sits under a pointer that
 * *does* have modifiers, and the row's own mousedown has already acted on them before the click
 * reaches here. See the guard on the circle's `onClick` below. Before the mode had a box of its
 * own, the box in that mode still mutated the task: the manager tapped one at 375 and started a
 * task while trying to gather three. A row with a selection box is not offering to complete
 * anything — the command bar the mode exists to fill is where the verbs are.
 */
export function TaskStatusControl({
  task,
  onAct,
  onFail,
  selection,
  modifierSelects = false,
}: {
  task: Task;
  /**
   * This box is about to change the task — told to whoever is keeping a cursor over the list,
   * with the status it is heading for so the list can name what happened.
   *
   * The list parks its cursor when the row you aimed at leaves, and only the keyboard used to say
   * so: a click here completed the row, the cursor slid onto whatever moved up into it, and the
   * next `x` acted on a task nobody had pointed at. See `holdGap` in `useTaskSelection`.
   *
   * The status is passed because `x` and this box now do the same thing, and `x` has always left
   * a readback naming the task and offering the way back. A click that completes a row silently
   * would be the one path to `completeTask` with no undo on it.
   *
   * What it returns is the claim it put up, handed back to `onFail` if this press is refused.
   */
  onAct?: (status: TaskStatus) => ReadbackClaim;
  /**
   * The change `onAct` announced did not happen — the server refused it.
   *
   * `onAct` fires before the request, deliberately (see `onMutate` below), so whoever put up a
   * readback on it has put up a claim the server has not yet agreed to. Without this a 500 left
   * *Started “X”.* standing, with a way back for a change that never happened
   * (`01a15e3c-61e3`). Called with whatever `onAct` returned for this same press, so the listener
   * can tell whether its readback is still the one about this press.
   *
   * The claim rather than the status or the row's id, which is what it was handed until
   * `01a16009-e4e1`: a later command naming the same row — a bulk Done over it and another, a
   * second press of the same Start — carries the same id and can carry the same status, so a
   * listener matching on either took the later, landed command's readback down with this one.
   *
   * No rollback of the row goes with it: `onSettled` refetches, and the server's copy is the
   * status the row had.
   */
  onFail?: (claim: ReadbackClaim) => void;
  /**
   * Present only while the list is in Select mode, and then the box selects instead of completing.
   *
   * A prop rather than a mode read from somewhere global, because three of the five surfaces that
   * draw this box have no selection at all and must not grow one by accident.
   */
  selection?: { selected: boolean; onToggle: () => void };
  /**
   * Something upstream already turns a shift, ctrl or cmd press on this box into a selection, so
   * a modified click here must not complete as well.
   *
   * Only `TaskList` passes it, because only its row has that mousedown handler. Today, the goal
   * tree, Projects and the board's done strip draw this box with nothing upstream, and a guard
   * keyed to the component rather than the list would make a modified press there do nothing.
   */
  modifierSelects?: boolean;
}) {
  const queryClient = useQueryClient();
  /*
    Where the last press was heading.

    Kept from the cycle, for a smaller reason than it had then: the mutation is not instant, and
    without it the tick disappears again for one round trip because `task.status` in props still
    says what it said before the click.
  */
  const pending = useRef<TaskStatus | null>(null);

  const move = useMutation({
    // Before the request, not after it: what matters is that the arming and the readback are in
    // place by the time the new id list arrives, and a refetch can land while this is in flight.
    // The price of announcing first is that a refusal has to be announced too — `onError`.
    onMutate: (status: TaskStatus) => onAct?.(status),
    // `onMutate`'s return is this press's context, so the refusal names the claim it refutes.
    onError: (_error, _status, claim) => onFail?.(claim),
    mutationFn: (status: TaskStatus) =>
      // Done keeps its own route: it spawns a recurring task's successor, logs the habit entry
      // and completes the block. Phase 36 made a plain status write equivalent; going through the
      // named one keeps them honest if they ever drift.
      /* `.task`: completing also answers the occurrence it created (`01a0809a-11db`), and this
         mutation is about the row that changed. */
      status === 'done'
        ? completeTask(task.id).then((r) => r.task)
        : updateTask(task.id, { status }),
    onSettled: () => {
      pending.current = null;
      void queryClient.invalidateQueries();
    },
  });

  if (selection) {
    /*
      A different shape, not a different colour.

      Selected and completed are both "a ticked box" and they mean opposite things — one is a row
      you are about to act on, the other a row that is finished — so the selection box is a circle
      and the completion box a rounded square. That survives greyscale and deuteranopia, which a
      border-colour difference does not: telling `rgb(107,107,102)` from `rgb(28,92,171)` was the
      whole of the old distinction and is exactly what the architect persona filed. The name says
      it too, so nothing rests on the picture alone.
    */
    const label = `${selection.selected ? 'Deselect' : 'Select'} ${task.title}`;
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={selection.selected}
        aria-label={label}
        title={label}
        className="state-box is-select"
        data-state={task.status}
        onClick={(e) => {
          /*
            A modifier press was already a selection gesture, on the row's mousedown in `TaskList`;
            `preventDefault` there does not stop the click, so without this the same press acted
            twice. Shift-click extended the range to this row and then this toggle unticked it —
            click circle 0, shift-click circle 5, and the bar read 5 with the row aimed at left out
            — and ctrl-click ticked the row and untoggled it again, doing nothing. The title button
            carries the same guard for the same reason.

            Here rather than by stopping the mousedown from reaching the row: the row's handler is
            the one place every part of the row, this circle included, turns a modifier into a
            selection, and it is the one the title and the row body already rely on. Only the list
            passes `selection`, and the list is where that row handler lives, so the guard cannot
            swallow a press on a surface with nothing upstream to handle it.
          */
          if (e.shiftKey || e.metaKey || e.ctrlKey) return;
          selection.onToggle();
        }}
      >
        {selection.selected && <Icon name="done" size={14} />}
      </button>
    );
  }

  const shown = pending.current ?? task.status;
  const isDone = shown === 'done';
  /*
    `Reopen` is hand-written and stays that way. It is the only control naming the move *out* of
    done, and "To next" would hide what is actually happening: completing ran the recurring
    successor, the habit entry and the block, and putting the status back reverses none of them —
    the same reason the list's undo is called Reopen rather than Undo. See `recordCommand` in
    `TaskList`.
  */
  const label = isDone ? `Reopen ${task.title}` : `${STATUS_VERB.done.verb} ${task.title}`;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isDone}
      aria-label={label}
      title={label}
      className="state-box"
      data-state={shown}
      data-busy={move.isPending || undefined}
      onClick={(e) => {
        /*
          A modifier press was already a selection gesture, on the row's mousedown in `TaskList`,
          and `preventDefault` there does not stop the click — the same double-fire the circle's
          guard above closes. Unguarded, one ctrl-click toggled the row's selection *and*
          completed the task, running its recurring successor, habit entry and block with it
          (`01a17665-8831`). A modified press on a task means "select", never "activate".

          Shift-click looked safe when it was probed, and that was luck rather than a guard. From
          a list with nothing selected, the press that brings the selection bar up pushes every
          row down by the bar's height between mousedown and mouseup — measured at 1280, the box
          went from y=415 to y=473 — so the mouseup lands on another row and the click goes to
          the list, not this box. Ctrl-click from a fresh list missed the same way. Once the bar
          is already up nothing moves, and both modifiers completed the task.

          Here, keyed to `modifierSelects`, rather than by stopping the mousedown from reaching the
          row, for the reason the circle gives: the row's handler is the one place every part of
          the row turns a modifier into a selection.
        */
        if (modifierSelects && (e.shiftKey || e.metaKey || e.ctrlKey)) return;
        // From where the last press was heading, not from the props, which lag it.
        const to: TaskStatus = (pending.current ?? task.status) === 'done' ? 'next' : 'done';
        pending.current = to;
        move.mutate(to);
      }}
    >
      {isDone && <Icon name="done" size={14} />}
    </button>
  );
}

/**
 * Whether the work has started — as an offer while it has not, and as a fact once it has.
 *
 * One slot, which until now only ever held the *static* "In progress" badge. That badge and the
 * old status box drew the identical path — `M5 3.5l7 4.5-7 4.5V3.5z` — three elements apart in the
 * same row, so the play triangle meant "this is running" in one place and "press me to finish" in
 * the other. The triangle means one thing now: `in_progress`. Pressing it puts the task there;
 * once it is there the same glyph stays, in the same place, as a mark rather than a button.
 *
 * **Pressable and painted are two different pictures, not two colours.** The first attempt at
 * that was outline-versus-bare — the offer drew a 1px border and the fact did not — and three
 * personas measured it as one picture, with the *offer* the louder of the two (`01a15a31-174b`).
 * So the fact is filled and the offer is a neutral outline: the state carries the weight, the
 * action does not, and the checkbox stays the loudest thing on an unstarted row. The accessible
 * names are "Start …" and "In progress — started", so a reader who cannot see the fill, or cannot
 * see at all, is told which of the two this is. That is the SC 1.4.1 bargain the icon
 * vocabulary already makes everywhere else: shape carries the meaning and tone only reinforces it.
 * The offer is deliberately *not* a `.badge`: a badge in this app is a state drawn, and this row
 * has no state to draw yet — see `.start-mark` in the stylesheet, and the goal screen's own spec,
 * which asks exactly that question of an unstarted row.
 *
 * Where it sits is per-surface and was measured rather than assumed; see the call sites. The short
 * version: below 40rem the task list's meta strip is a *line of its own*, usually empty, so a 24px
 * control in it takes the row from 43px to 67 — so on Tasks it is a fixed right-hand column beside
 * the title-and-meta block, which adds no line; on Today it stays in the shortlist's meta strip.
 *
 * Nothing at all on a `done` or `dropped` row. That resolves the dropped-task defect by removing
 * its subject rather than renaming it: the box on a dropped row used to be labelled "Start …" and
 * moved the task to `next`, promising one act and performing another. No control on the row says
 * "Start" now unless pressing it really does start the task; `2` and the command bar's "To next"
 * are how a dropped task is restored, and they say so.
 *
 * `blocked` outranks both, exactly as it does everywhere `displayStatus` is asked: "next, but
 * blocked by something" is one fact and the useful half of it is the blocker, and offering to
 * start work that cannot start would be the row arguing with the pill beside it.
 */
export function TaskProgressMark({
  task,
  onAct,
  onFail,
}: {
  task: Task;
  /** Same bargain as the box above: the cursor parks, and the list names what just happened. */
  onAct?: (status: TaskStatus) => ReadbackClaim;
  /** And the same other half: the Start announced before the request was refused. */
  onFail?: (claim: ReadbackClaim) => void;
}) {
  const queryClient = useQueryClient();
  const start = useMutation({
    onMutate: () => onAct?.('in_progress'),
    onError: (_error, _vars, claim) => onFail?.(claim),
    mutationFn: () => updateTask(task.id, { status: 'in_progress' }),
    onSettled: () => void queryClient.invalidateQueries(),
  });

  const shown = displayStatus(task);
  if (shown === 'blocked') return <StatusBadge status={shown} compact />;
  if (shown === 'in_progress') {
    /*
      The fact carries the weight: filled in the status hue, the glyph knocked out of it.

      It was `StatusBadge compact` — a bare glyph in the accent colour — beside an offer drawn as
      the same glyph in the same colour with a 1px outline. Three personas measured that as one
      picture. So the state is now the solid one and the offer the quiet one (`.start-mark`), the
      inverse of what they were, and the word *started* is in the name so the tooltip says what
      the fill means. Not a change to `StatusBadge`: the board, the picker and the detail panel
      draw that one where nothing beside it competes, and this is the one slot with a twin.
    */
    const label = `${STATUS_LOOK.in_progress.label} — started`;
    return (
      <span className="badge mark-only tone-accent is-started" title={label}>
        <Icon name={STATUS_LOOK.in_progress.icon} size={15} label={label} />
      </span>
    );
  }
  if (task.status !== 'inbox' && task.status !== 'next') return null;

  const label = `${STATUS_VERB.in_progress.verb} ${task.title}`;
  return (
    <button
      type="button"
      className="start-mark"
      aria-label={label}
      title={label}
      data-busy={start.isPending || undefined}
      onClick={() => start.mutate()}
    >
      <Icon name={STATUS_LOOK.in_progress.icon} size={15} />
    </button>
  );
}
